// HTTP 429 is temporary throttling, never evidence of an invalid SSO session.
export const RATE_LIMIT_EVENT = 'lms:rate-limited';
export const DEFAULT_RETRY_SECONDS = 60;
export const MAX_RETRY_SECONDS = 3600;
let retryAt = 0;

type RateLimitError = { response?: { status?: number; headers?: any; data?: any }; message?: string };
export function isRateLimited(error: unknown): boolean {
  return (error as RateLimitError | null)?.response?.status === 429;
}

/** Accept delta-seconds and HTTP dates; cap unreasonable values and never spin at 0. */
export function parseRetryAfter(value: unknown, now = Date.now()): number | null {
  let seconds: number;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    seconds = value;
  } else if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    seconds = Number(value.trim());
  } else if (typeof value === 'string' && /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), /i.test(value)) {
    const date = Date.parse(value);
    if (!Number.isFinite(date)) return null;
    seconds = (date - now) / 1000;
  } else {
    return null;
  }
  return Math.max(1, Math.min(MAX_RETRY_SECONDS, Math.ceil(seconds)));
}

export function retryAfterSeconds(error: unknown, now = Date.now()): number {
  const response = (error as RateLimitError | null)?.response;
  const headers = response?.headers;
  const header = typeof headers?.get === 'function'
    ? headers.get('retry-after')
    : headers?.['retry-after'] ?? headers?.['Retry-After'];
  return parseRetryAfter(header, now)
    ?? parseRetryAfter(response?.data?.retryAfterSeconds, now)
    ?? parseRetryAfter(response?.data?.retryAfter, now)
    ?? DEFAULT_RETRY_SECONDS;
}

export function rateLimitMessage(seconds: number): string {
  return `Kısa sürede çok fazla istek gönderildi. Lütfen ${seconds} saniye sonra tekrar deneyin.`;
}

export function getRateLimitUntil(): number { return retryAt; }

/** Keep the original rejected error/response; never replay a request, including POSTs. */
export function handleRateLimit(error: unknown): void {
  if (!isRateLimited(error)) return;
  const seconds = retryAfterSeconds(error);
  const err = error as RateLimitError;
  const message = rateLimitMessage(seconds);
  err.message = message;
  // Existing form callers read response.data.error; edge-generated HTML also needs guidance.
  if (err.response) {
    const data = err.response.data;
    err.response.data = {
      ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {}),
      code: 'RATE_LIMITED', error: message, retryAfterSeconds: seconds,
    };
  }
  retryAt = Math.max(retryAt, Date.now() + seconds * 1000);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<number>(RATE_LIMIT_EVENT, { detail: retryAt }));
  }
}
